import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Tooltip,
  Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { parseISO, format, differenceInCalendarDays, startOfToday, startOfWeek, endOfWeek, isWithinInterval } from 'date-fns';
import { AppContext } from '../AppContext';
import DashCalendar, { taskEmojiMap, taskColorMap } from '../components/DashCalendar';
import ConfirmationDialog from '../components/ConfirmationDialog';
import InfoModalV2 from '../components/InfoModalV2';
import { SnackbarContext } from '../SnackbarContext';
import { deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import GrassIcon from '@mui/icons-material/Grass';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { Link } from 'react-router-dom';
import { createHarvestSchema } from '../modals/HarvestSchema';
import { createOrderSchema } from '../modals/OrderSchema';

/**
 * Modern, mobile‑first dashboard that replaces wide tables with:
 *  - KPI stat cards (Harvests, Deliveries, Active Tasks, Revenue)
 *  - Compact lists for "Next Harvests" and "Upcoming Deliveries"
 *  - Calendar for task overview (kept)
 * Users can navigate to full pages for detailed tables.
 */

function StatCard({ icon, label, value, helper, color }) {
  const theme = useTheme();
  return (
    <Card elevation={3} sx={{ height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: color || theme.palette.primary.main }}>{icon}</Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="overline" sx={{ opacity: 0.8, lineHeight: 1.1 }}>{label}</Typography>
          <Typography variant="h4" noWrap>{value}</Typography>
          {helper && (
            <Typography variant="caption" sx={{ opacity: 0.7, lineHeight: 1.2 }}>{helper}</Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function Pill({ children, color = 'default' }) {
  return (
    <Chip size="small" color={color === 'default' ? undefined : color} label={children} sx={{ borderRadius: 1 }} />
  );
}

function Dashboard() {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const { user, userData, orders, harvests, tasks, seeds, trays, farmData } = useContext(AppContext);
  const activeFarmId = userData?.activeFarmId;
  const { showSuccess, showError } = useContext(SnackbarContext);

  const farmName = farmData?.farmName || userData?.farmName || 'Farm Name';
  const defaultDateFormat = farmData?.settings?.dateFormat || 'dd-MM-yyyy';
  const shortFormat = defaultDateFormat.includes('yyyy')
    ? defaultDateFormat.replace(/[-/]?yyyy/, '')
    : defaultDateFormat;

  const formatTaskLabel = (task) => {
    const emoji = task.emoji || taskEmojiMap[task.type] || '';
    const baseDate = task.harvestStartDate
      ? parseISO(task.harvestStartDate)
      : toDate(task.date);
    const suffix = task.type !== 'delivery' && baseDate
      ? ` (${format(baseDate, shortFormat)})`
      : '';
    return `${emoji} ${task.label || task.type}${suffix}`.trim();
  };

  // ----- Lookups -----
  const seedLookup = useMemo(() => {
    return (seeds || []).reduce((acc, s) => { acc[s.id] = s; return acc; }, {});
  }, [seeds]);
  const trayLookup = useMemo(() => {
    return (trays || []).reduce((acc, t) => { acc[t.id] = t; return acc; }, {});
  }, [trays]);
  const harvestLookup = useMemo(() => {
    return (harvests || []).reduce((acc, h) => { acc[String(h.id)] = h; return acc; }, {});
  }, [harvests]);

  // ----- Helpers -----
  const toDate = (value) => {
    if (!value) return null;
    if (value.toDate) return value.toDate();
    if (value instanceof Date) return value;
    if (typeof value === 'string') {
      const d = parseISO(value);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  const getSeedDisplay = (harvest) => {
    if (Array.isArray(harvest.seeds) && harvest.seeds.length) {
      return harvest.seeds.map(s => (s.seedId && seedLookup[s.seedId] ? seedLookup[s.seedId].name : 'Unknown')).join('/');
    }
    if (harvest.seedId && seedLookup[harvest.seedId]) return seedLookup[harvest.seedId].name;
    return 'Unknown';
  };

  const getTrayDisplay = (harvest) => {
    const trayPrefix = harvest.trayNumber ? `${harvest.trayNumber}x ` : '';
    const trayId = harvest.trayId || (harvest.seeds && harvest.seeds[0]?.trayId);
    const t = trayId ? trayLookup[trayId] : null;
    if (t) {
      if (t.length && t.width) return trayPrefix + `${t.brand || ''} ${t.length}${t.lengthUnit}×${t.width}${t.widthUnit}`;
      return trayPrefix + (t.brand || 'Tray');
    }
    return trayPrefix + (harvest.trays || 'Tray');
  };

  const getYieldDisplay = (harvest) => (harvest.yieldValue ? `${harvest.yieldValue}${harvest.yieldUnit ? ` ${harvest.yieldUnit}` : ''}` : '');
  const getPortions = (h) => (h.portions ?? h.seeds?.[0]?.portions ?? '');
  const getPortionsLeft = (h) => (h.portionsLeft ?? h.seeds?.[0]?.portionsLeft ?? '');

  // Orders helpers
  const computeProduceTotal = (order) => {
    if (!order?.produce || !Array.isArray(order.produce)) return 0;
    return order.produce.reduce((sum, p) => sum + (parseFloat(p.price || 0) * parseFloat(p.portions || 0)), 0);
  };
  const getDeliveryPrice = (order) => parseFloat(order?.deliveryPrice || 0);
  const getDeliveryCurrency = (order) => order?.deliveryPriceCurrency || order?.produce?.[0]?.priceCurrency || '£';

  const formatProduceNames = (order) => {
    if (Array.isArray(order?.produce)) {
      return order.produce.map((prod) => {
        const harvestIds = Array.isArray(prod.items) ? prod.items.map(it => it.harvestId) : (prod.harvestId ? [prod.harvestId] : []);
        const labels = harvestIds.map((id) => {
          const h = harvestLookup[String(id)];
          if (!h) return 'Deleted';
          const dateLabel = h.harvestDate ? format(parseISO(h.harvestDate), shortFormat) : 'No Date';
          let seedName = 'Unknown';
          if (Array.isArray(h.seeds) && h.seeds.length) {
            seedName = h.seeds.map(s => (s.seedId && seedLookup[s.seedId] ? seedLookup[s.seedId].name : 'Unknown')).join('/');
          } else if (h.seedId && seedLookup[h.seedId]) seedName = seedLookup[h.seedId].name;
          return `${seedName}(${dateLabel})`;
        });
        return labels.join('/');
      }).join(', ');
    }
    return '';
  };

  // ----- Sort & slice for compact lists -----
  const sortedHarvests = useMemo(() => {
    return [...(harvests || [])].sort((a, b) => {
      const dA = toDate(a.harvestDate) || new Date(0);
      const dB = toDate(b.harvestDate) || new Date(0);
      return dA - dB;
    });
  }, [harvests]);

  const upcomingHarvests = useMemo(() => {
    return sortedHarvests
      .filter(h => toDate(h.harvestDate) && !h.harvested)
      .slice(0, 6);
  }, [sortedHarvests]);

  const recentlyHarvested = useMemo(() => {
    return sortedHarvests
      .filter(h => toDate(h.harvestDate) && h.harvested)
      .sort((a, b) => {
        const dA = toDate(a.harvestDate) || new Date(0);
        const dB = toDate(b.harvestDate) || new Date(0);
        return dB - dA;
      })
      .slice(0, 6);
  }, [sortedHarvests]);

  const sortedOrders = useMemo(() => {
    return [...(orders || [])].sort((a, b) => {
      const dA = toDate(a.deliveryDate) || new Date(0);
      const dB = toDate(b.deliveryDate) || new Date(0);
      return dA - dB;
    });
  }, [orders]);

  const upcomingOrders = useMemo(() => {
    const today = startOfToday();
    return sortedOrders.filter(o => {
      const d = toDate(o.deliveryDate);
      return d && differenceInCalendarDays(d, today) >= 0;
    }).slice(0, 6);
  }, [sortedOrders]);

  const sortedTasks = useMemo(() => {
    return [...(tasks || [])].sort((a, b) => {
      const dA = toDate(a.date) || new Date(0);
      const dB = toDate(b.date) || new Date(0);
      const byDate = dA - dB;
      if (byDate !== 0) return byDate;
      const prio = { delivery: 1, harvest: 2, lights: 3, blackout: 4, sow: 5, water: 6 };
      return (prio[a.type] || 99) - (prio[b.type] || 99);
    });
  }, [tasks]);

  const todayTasks = useMemo(() => {
    const today = startOfToday();
    return sortedTasks.filter(t => {
      const dd = toDate(t.date);
      if (!dd) return false;
      return differenceInCalendarDays(dd, today) === 0;
    });
  }, [sortedTasks]);

  const todayTaskGroups = useMemo(() => {
    const groups = {};
    todayTasks.forEach((t) => {
      const key = `${t.type}_${t.label || ''}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    });
    return Object.entries(groups).map(([key, tasks]) => ({
      key,
      tasks,
      label: tasks.map((task) => formatTaskLabel(task)).join('\n'),
    }));
  }, [todayTasks, formatTaskLabel]);

  const weeklyOrders = useMemo(() => {
    const weekStartsOn = farmData?.settings?.calendarWeekStart === 'monday' ? 1 : 0;
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn });
    const weekEnd = endOfWeek(now, { weekStartsOn });
    return sortedOrders.filter((o) => {
      const d = toDate(o.deliveryDate);
      return d && isWithinInterval(d, { start: weekStart, end: weekEnd });
    });
  }, [sortedOrders, farmData]);

  const grandTotal = useMemo(() => {
    return weeklyOrders.reduce((sum, o) => sum + computeProduceTotal(o) + getDeliveryPrice(o), 0);
  }, [weeklyOrders]);

  // ----- Actions / dialogs -----
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTasks, setConfirmTasks] = useState([]);
  const openConfirmDialog = (taskOrGroup) => {
    const group = Array.isArray(taskOrGroup) ? taskOrGroup : [taskOrGroup];
    setConfirmTasks(group);
    setConfirmOpen(true);
  };
  const handleConfirmAction = async () => {
    if (!user || confirmTasks.length === 0) return;
    if (!activeFarmId) {
      showError('No active farm selected.');
      return;
    }
    try {
      for (const t of confirmTasks) {
        await deleteDoc(doc(db, 'farms', activeFarmId, 'tasks', t.id));
      }
      showSuccess(`${confirmTasks.length} task${confirmTasks.length > 1 ? 's' : ''} completed.`);
    } catch (e) {
      showError('Error completing task(s).');
    } finally {
      setConfirmOpen(false);
      setConfirmTasks([]);
    }
  };

  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [modalSchema, setModalSchema] = useState(null);

  const formatDate = useCallback((value) => {
    const d = toDate(value);
    return d ? format(d, defaultDateFormat) : '';
  }, [defaultDateFormat]);

  const harvestSchema = useMemo(() => {
    return createHarvestSchema({
      formatDate,
      getSeedDisplay,
      getTrayDisplay,
      getYieldDisplay,
      getPortionsLeft,
    });
  }, [formatDate, getSeedDisplay, getTrayDisplay, getYieldDisplay, getPortionsLeft]);

  const orderSchema = useMemo(() => {
    return createOrderSchema({
      formatDate,
      formatProduceNames,
      computeProduceTotal,
      getDeliveryPrice,
      getDeliveryCurrency,
      toDate,
    });
  }, [formatDate, formatProduceNames, computeProduceTotal, getDeliveryPrice, getDeliveryCurrency, toDate]);

  const openHarvestModal = (h) => {
    setSelectedData(h);
    setModalSchema(harvestSchema);
    setInfoModalOpen(true);
  };

  const openOrderModal = (o) => {
    setSelectedData(o);
    setModalSchema(orderSchema);
    setInfoModalOpen(true);
  };
  // KPI values
  const activeHarvestsCount = (harvests || []).filter(h => h.harvestDate && !h.harvested).length;
  const upcomingDeliveriesCount = upcomingOrders.length;
  const activeTasksCount = (tasks || []).length;

  return (
    <Box
      sx={{
        maxWidth: '1200px',
        mx: 'auto',
        px: { xs: 1.5, sm: 3 },
        py: 2,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant={isSmDown ? 'h5' : 'h4'} sx={{ color: theme.palette.warning.main }}>{farmName}</Typography>
      </Box>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard icon={<GrassIcon />} label="Active Harvests" value={activeHarvestsCount} helper="Not yet marked harvested" color={theme.palette.success.main} />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard icon={<LocalShippingIcon />} label="Upcoming Deliveries" value={upcomingDeliveriesCount} helper="Next 6 shown" color={theme.palette.info.main} />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard icon={<TaskAltIcon />} label="Active Tasks" value={activeTasksCount} helper="Across all dates" color={theme.palette.secondary.main} />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard icon={<MonetizationOnIcon />} label="Projected Total" value={`${getDeliveryCurrency(sortedOrders[0] || {})}${grandTotal.toFixed(2)}`} helper="Current week" color={theme.palette.primary.main} />
        </Grid>
      </Grid>

      {/* Mobile-only: calendar just below KPIs */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <Card elevation={1} sx={{ mb: 2 }}>
          <CardHeader title="Next 7 days" />
          <CardContent>
            <DashCalendar tasks={sortedTasks} onTaskClick={openConfirmDialog} initialView="dayGridWeek" rollingWeek />
          </CardContent>
        </Card>
      </Box>

      <Grid container spacing={2}>
        {/* Left column: Compact lists */}
        <Grid item xs={12} md={6}>
          {/* Today quick glance moved here to balance columns */}
          {/*
          <Card elevation={1} sx={{ mb: 2 }}>
            <CardHeader title="Today" subheader="Quick glance" />
            <CardContent sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {todayTaskGroups.slice(0, 12).map((group) => {
                const firstTask = group.tasks[0];
                const taskDate = firstTask ? toDate(firstTask.date) : null;
                const overdue = taskDate ? differenceInCalendarDays(taskDate, startOfToday()) < 0 : false;
                const isToday = taskDate ? differenceInCalendarDays(taskDate, startOfToday()) === 0 : false;
                const chipSx = {
                  fontSize: '0.7rem',
                  padding: '2px 6px',
                  height: 'auto',
                  borderRadius: '12px',
                  '& .MuiChip-label': {
                    padding: '3px 0',
                    whiteSpace: 'pre-line',
                    textAlign: 'left',
                  },
                  ...(overdue
                    ? { border: `2px solid ${theme.palette.error.main}` }
                    : isToday
                      ? { border: `2px solid ${theme.palette.secondary.main}` }
                      : {}),
                };
                const isCustom = firstTask?.type === 'custom';
                return (
                  <Tooltip
                    key={group.key}
                    title={<Typography sx={{ fontSize: '0.8rem' }}>Type: {firstTask?.type}</Typography>}
                    arrow
                    placement="top"
                  >
                    <Chip
                      size="small"
                      label={group.label}
                      variant="filled"
                      color={isCustom ? undefined : taskColorMap[firstTask?.type]}
                      sx={{
                        backgroundColor: isCustom ? firstTask?.color || theme.palette.grey[700] : undefined,
                        color: isCustom
                          ? theme.palette.getContrastText(firstTask?.color || theme.palette.grey[700])
                          : undefined,
                        ...chipSx,
                      }}
                      onClick={() => openConfirmDialog(group.tasks)}
                    />
                  </Tooltip>
                );
              })}
              {todayTaskGroups.length === 0 && (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>No tasks today.</Typography>
              )}
            </CardContent>
          </Card>
          */}

          <Card elevation={1} sx={{ mb: 2 }}>
            <CardHeader title="Next Harvests" subheader="Tap a row for details" />
            <CardContent sx={{ pt: 0 }}>
              {upcomingHarvests.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>No upcoming harvests.</Typography>
              ) : (
                <List disablePadding>
                  {upcomingHarvests.map((h) => {
                    const d = toDate(h.harvestDate);
                    const today = d ? differenceInCalendarDays(d, startOfToday()) === 0 : false;
                    const overdue = d ? differenceInCalendarDays(d, startOfToday()) < 0 : false;
                    const days = d ? differenceInCalendarDays(d, startOfToday()) : 0;
                    const when = days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Today' : `${days}d`;
                    return (
                      <React.Fragment key={h.id}>
                        <ListItem disablePadding>
                          <ListItemButton onClick={() => openHarvestModal(h)} sx={{ py: 1 }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Pill color={overdue ? 'warning' : today ? 'secondary' : 'default'}>
                                    {d ? format(d, defaultDateFormat) : '--'}
                                  </Pill>
                                  <Typography variant="subtitle2" noWrap>{getSeedDisplay(h)}</Typography>
                                </Box>
                              }
                              secondary={
                                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                                  {getTrayDisplay(h)} | {getYieldDisplay(h)} | Portions {getPortions(h)} | Left {Number(getPortionsLeft(h) || 0).toFixed(2)}
                                </Typography>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Typography variant="caption" sx={{ opacity: 0.7 }}>{when}</Typography>
                            </ListItemSecondaryAction>
                          </ListItemButton>
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button size="small" variant="text" component={Link} to="/harvests">View all</Button>
              </Box>
            </CardContent>
          </Card>

          <Card elevation={1} sx={{ mb: 2 }}>
            <CardHeader title="Upcoming Deliveries" subheader="Tap a row for details" />
            <CardContent sx={{ pt: 0 }}>
              {upcomingOrders.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>No scheduled deliveries.</Typography>
              ) : (
                <List disablePadding>
                  {upcomingOrders.map((o) => {
                    const d = toDate(o.deliveryDate);
                    const days = d ? differenceInCalendarDays(d, new Date()) : 0;
                    const when = days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Today' : `${days}d`;
                    const total = `${getDeliveryCurrency(o)}${(computeProduceTotal(o) + getDeliveryPrice(o)).toFixed(2)}`;
                    return (
                      <React.Fragment key={o.id}>
                        <ListItem disablePadding>
                          <ListItemButton onClick={() => openOrderModal(o)} sx={{ py: 1 }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Pill color={days < 0 ? 'warning' : days === 0 ? 'secondary' : 'default'}>
                                    {d ? format(d, defaultDateFormat) : '--'}
                                  </Pill>
                                  <Typography variant="subtitle2" noWrap>
                                    {o.orderNumber ? `#${o.orderNumber} - ` : ''}{o.customerName}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                                  {formatProduceNames(o)}
                                </Typography>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Typography variant="subtitle2">{total}</Typography>
                              <Typography variant="caption" sx={{ opacity: 0.7 }}>{when}</Typography>
                            </ListItemSecondaryAction>
                          </ListItemButton>
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button size="small" variant="text" component={Link} to="/orders">View all</Button>
              </Box>
            </CardContent>
          </Card>

        </Grid>

        {/* Right column: Calendar + Today quick list */}
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ mb: 2, display: { xs: 'none', md: 'block' } }}>
            <CardHeader title="Today" subheader="What needs doing now" />
            <CardContent sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {todayTaskGroups.slice(0, 12).map((group) => {
                const firstTask = group.tasks[0];
                const taskDate = firstTask ? toDate(firstTask.date) : null;
                const overdue = taskDate ? differenceInCalendarDays(taskDate, startOfToday()) < 0 : false;
                const isToday = taskDate ? differenceInCalendarDays(taskDate, startOfToday()) === 0 : false;
                const isCustom = firstTask?.type === 'custom';
                const label = group.tasks.length > 1
                  ? `${formatTaskLabel(firstTask)} +${group.tasks.length - 1}`
                  : formatTaskLabel(firstTask);
                const mappedColor = taskColorMap[firstTask?.type];
                const taskBgColor = mappedColor
                  ? (
                    firstTask?.type === 'blackout'
                      ? (theme.palette[mappedColor]?.light || theme.palette[mappedColor]?.main)
                      : (theme.palette[mappedColor]?.dark || theme.palette[mappedColor]?.main)
                  )
                  : theme.palette.grey[700];

                return (
                  <Tooltip
                    key={group.key}
                    title={
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        {group.tasks.length > 1 ? `${group.tasks.length} tasks` : firstTask?.type}
                      </Typography>
                    }
                    arrow
                    placement="top"
                  >
                    <Box
                      component="button"
                      type="button"
                      onClick={() => openConfirmDialog(group.tasks)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openConfirmDialog(group.tasks);
                        }
                      }}
                      sx={{
                        border: 'none',
                        cursor: 'pointer',
                        px: 1,
                        py: 0.75,
                        minHeight: 52,
                        minWidth: 110,
                        maxWidth: 210,
                        borderRadius: '10px',
                        textAlign: 'left',
                        transition: 'transform 0.08s ease, filter 0.12s ease',
                        backgroundColor: isCustom
                          ? firstTask?.color || theme.palette.grey[700]
                          : taskBgColor,
                        color: isCustom
                          ? theme.palette.getContrastText(firstTask?.color || theme.palette.grey[700])
                          : theme.palette.getContrastText(taskBgColor),
                        '&:hover': {
                          filter: 'brightness(1.04)',
                        },
                        '&:active': {
                          transform: 'scale(0.98)',
                        },
                        '&:focus-visible': {
                          outline: `2px solid ${theme.palette.warning.main}`,
                          outlineOffset: 2,
                        },
                        ...(overdue
                          ? { boxShadow: `0 0 0 2px ${theme.palette.error.main} inset` }
                          : isToday
                            ? { boxShadow: `0 0 0 2px ${theme.palette.secondary.main} inset` }
                            : {}),
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontSize: '0.86rem',
                          lineHeight: 1.3,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          fontWeight: 500,
                        }}
                      >
                        {label}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
              {todayTaskGroups.length === 0 && (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  No tasks today.
                </Typography>
              )}
            </CardContent>
          </Card>

          <Card elevation={1} sx={{ mb: 2, display: { xs: 'none', md: 'block' } }}>
            <CardHeader title="Next 7 days" />
            <CardContent>
              <DashCalendar tasks={sortedTasks} onTaskClick={openConfirmDialog} initialView="dayGridWeek" rollingWeek />
            </CardContent>
          </Card>

          <Card elevation={1}>
            <CardHeader title="Recently Harvested" subheader="Tap a row for details" />
            <CardContent sx={{ pt: 0 }}>
              {recentlyHarvested.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>No harvested records.</Typography>
              ) : (
                <List disablePadding>
                  {recentlyHarvested.map((h) => {
                    const d = toDate(h.harvestDate);
                    return (
                      <React.Fragment key={h.id}>
                        <ListItem disablePadding>
                          <ListItemButton onClick={() => openHarvestModal(h)} sx={{ py: 1 }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Pill color="primary">
                                    {d ? format(d, defaultDateFormat) : '--'}
                                  </Pill>
                                  <Typography variant="subtitle2" noWrap>{getSeedDisplay(h)}</Typography>
                                </Box>
                              }
                              secondary={
                                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                                  {getTrayDisplay(h)} | {getYieldDisplay(h)} | Portions {getPortions(h)}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button size="small" variant="text" component={Link} to="/harvests">View all</Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <ConfirmationDialog
        open={confirmOpen}
        title={`Complete ${confirmTasks.length > 1 ? 'Tasks' : 'Task'}`}
        message={confirmTasks.length > 1 ? 'Mark task(s) complete?' : 'Mark task complete?'}
        onConfirm={handleConfirmAction}
        onClose={() => { setConfirmOpen(false); setConfirmTasks([]); }}
      />

      <InfoModalV2
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        data={selectedData}
        schema={modalSchema}
      />
    </Box>
  );
}

export default Dashboard;

// Utility retained from your original file for gating (kept here for completeness)
export function isSubscriptionActive(userData) {
  if (!userData || !userData.accessExpires) return true;
  const now = new Date();
  const expires = userData.accessExpires.toDate
    ? userData.accessExpires.toDate()
    : new Date(userData.accessExpires);
  return now <= expires;
}





